import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LookupCategory, ILookupValue } from '../interfaces/ILookUp';
import { ICustomProfileField } from '../interfaces/ICustomProfileField';
import { ICompany } from '../interfaces/ICompany';

interface LookupState {
    values: Record<LookupCategory, ILookupValue[]>;
    customProfileFields: ICustomProfileField[];
    company: ICompany | null;
    loading: boolean;
    error: string | null;
}

const createInitialLookupValues = (category: string, count: number): ILookupValue[] => {
    return Array.from({ length: count }, (_, i) => ({
        id: `${i + 1}`,
        name: `${category} ${i + 1}`,
        description: `Description for ${category} ${i + 1}`,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    }));
};

const createInitialDelegateRoles = (): ILookupValue[] => {
    return [
        {
            id: '',
            name: '',
            description: '',
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            delegateRoleGroupId: '', // VIP
        },
    ];
};


const createInitialRoleGroups = (): ILookupValue[] => {
    return [
        {
            id: '',
            name: '',
            description: '',
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        },

    ];
};

const initialState: LookupState = {
    values: {
        EventSessionTypes: createInitialLookupValues('Session Type', 5),
        DelegateRoleGroups: createInitialRoleGroups(),
        DelegateRoles: createInitialDelegateRoles(),
    },
    customProfileFields: [],
    company: null,
    loading: false,
    error: null,
};

const lookupSlice = createSlice({
    name: 'lookup',
    initialState,
    reducers: {
        addLookupValue: (
            state,
            action: PayloadAction<{ category: LookupCategory; value: ILookupValue }>
        ) => {
            const { category, value } = action.payload;
            state.values[category].push(value);
        },
        setLookupValues: (
            state,
            action: PayloadAction<{ category: LookupCategory; values: ILookupValue[] }>
        ) => {
            const { category, values } = action.payload;
            state.values[category] = values;
        },
        updateLookupValue: (
            state,
            action: PayloadAction<{ category: LookupCategory; value: ILookupValue }>
        ) => {
            const { category, value } = action.payload;
            const index = state.values[category].findIndex((v: { id: any; }) => v.id === value.id);
            if (index !== -1) {
                state.values[category][index] = value;
            }
        },
        toggleLookupValueActive: (
            state,
            action: PayloadAction<{ category: LookupCategory; id: string }>
        ) => {
            const { category, id } = action.payload;
            const index = state.values[category].findIndex((v: { id: string; }) => v.id === id);
            if (index !== -1) {
                state.values[category][index].isActive = !state.values[category][index].isActive;
                state.values[category][index].updatedAt = new Date().toISOString();
            }
        },
        deleteLookupValue: (
            state,
            action: PayloadAction<{ category: LookupCategory; id: string }>
        ) => {
            const { category, id } = action.payload;
            state.values[category] = state.values[category].filter((v: { id: string; }) => v.id !== id);
        },
        setCustomProfileFields(state, action) {
            state.customProfileFields = action.payload;
        },
        addCustomProfileField(state, action) {
            state.customProfileFields.push(action.payload);
        },
        updateCustomProfileField(state, action) {
            const idx = state.customProfileFields.findIndex(f => f.id === action.payload.id);
            if (idx !== -1) state.customProfileFields[idx] = action.payload;
        },
        deleteCustomProfileField(state, action) {
            state.customProfileFields = state.customProfileFields.filter(f => f.id !== action.payload);
        },
        reorderCustomProfileFields: (state, action: PayloadAction<{ delegateRoleId: string; fields: ICustomProfileField[] }>) => {
            const { delegateRoleId, fields } = action.payload;
            // Update sort order for the reordered fields
            fields.forEach((field, index) => {
                const existingFieldIndex = state.customProfileFields.findIndex(f => f.id === field.id);
                if (existingFieldIndex !== -1) {
                    state.customProfileFields[existingFieldIndex].sortOrder = index;
                }
            });
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setCompany: (state, action: PayloadAction<ICompany>) => {
            state.company = action.payload;
        },
    },
});
export const {
    addLookupValue,
    updateLookupValue,
    toggleLookupValueActive,
    deleteLookupValue,
    setCustomProfileFields,
    addCustomProfileField,
    updateCustomProfileField,
    deleteCustomProfileField,
    reorderCustomProfileFields,
    setLoading,
    setError,
    setLookupValues,
    setCompany
} = lookupSlice.actions;


export default lookupSlice.reducer;